import React, { useEffect, useMemo, useState } from 'react';

import { connect } from 'react-redux';

import { AppWrapper } from 'components';
import AffiliateCouponsList from 'components/AffiliateCouponsList/AffiliateCouponsList';
import Breadcrumbs from 'components/BreadCrumbs/BreadCrumbs';
import { actions as couponActions } from 'coupon-common/src/modules/coupons';
import { dispatchAsAsync } from 'coupon-common/src/utils';

import styles from './AffiliateCouponViewer.module.scss';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    componentDidCatch(error, info) {
        console.error('[AffiliateCouponViewer] Render error:', error, info);
    }
    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: 16, background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 4, marginBottom: 12 }}>
                    <strong>Failed to render: {this.props.businessName}</strong> — {this.state.error && this.state.error.message}
                </div>
            );
        }
        return this.props.children;
    }
}

const safeDate = (val) => {
    if (!val) return new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
    const d = new Date(val);
    return isNaN(d.getTime()) ? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000) : d;
};

const AffiliateCouponViewer = ({ dispatch, affiliateCoupons, affiliateBusinessData }) => {
    const [selectedBusinessId, setSelectedBusinessId] = useState('all');
    const [deletedIds, setDeletedIds] = useState(new Set());
    const [deleteStatus, setDeleteStatus] = useState(null);

    useEffect(() => {
        if (!affiliateCoupons || (!affiliateCoupons.fetching && !affiliateCoupons.fetched)) {
            dispatch(couponActions.fetchAllAffiliateCoupons());
        }
    }, []);

    const handleDelete = async (couponId) => {
        if (!window.confirm('Are you sure you want to delete this coupon?')) return;

        setDeleteStatus({ id: couponId, pending: true });

        const { success } = await dispatchAsAsync(
            couponActions.deleteCoupon(couponId),
            dispatch
        );

        if (success) {
            setDeletedIds(prev => new Set([...prev, couponId]));
            setDeleteStatus({ id: couponId, success: true, message: 'Coupon deleted successfully.' });
        } else {
            setDeleteStatus({ id: couponId, success: false, message: 'Failed to delete coupon.' });
        }

        setTimeout(() => setDeleteStatus(null), 3000);
    };

    const businessLookup = useMemo(() => {
        const results = (affiliateBusinessData && affiliateBusinessData.results) || affiliateBusinessData || [];
        const lookup = {};
        if (Array.isArray(results)) {
            results.forEach(b => { lookup[b.id] = b; });
        }
        return lookup;
    }, [affiliateBusinessData]);

    const allCoupons = useMemo(() => {
        if (!affiliateCoupons || !affiliateCoupons.byBusiness) return [];
        return Object.entries(affiliateCoupons.byBusiness).flatMap(([businessId, state]) => {
            if (!state.fetched || !state.data) return [];
            const coupons = Array.isArray(state.data) ? state.data : (state.data.results || []);
            return coupons.map(coupon => ({
                ...coupon,
                published_at: safeDate(coupon.published_at),
                expiration_date: safeDate(coupon.expiration_date),
                expiry_type: coupon.expiry_type || (coupon.expiration_date ? 'date' : 'quantity'),
                image_url: Array.isArray(coupon.image_url)
                    ? coupon.image_url
                    : coupon.image_url
                        ? [{ image_url: coupon.image_url, sequence: 1 }]
                        : [],
                localId: coupon.id || `${businessId}-${coupon.id}`,
                // _businessId: businessId,
                business_id: businessId,
                _businessName: (businessLookup[businessId] && businessLookup[businessId].name) || `Business #${businessId}`,
                // auto_accept: true
            }));
        });
    }, [affiliateCoupons, businessLookup]);

    const businessesWithCoupons = useMemo(() => {
        if (!affiliateCoupons || !affiliateCoupons.byBusiness) return [];
        return Object.entries(affiliateCoupons.byBusiness)
            .filter(([, state]) => state.fetched && state.data)
            .map(([businessId, state]) => {
                const coupons = Array.isArray(state.data) ? state.data : (state.data.results || []);
                return {
                    id: businessId,
                    affiliate_code: businessLookup[businessId]?.affiliate_code || "",
                    name: (businessLookup[businessId] && businessLookup[businessId].name) || `Business #${businessId}`,
                    count: coupons.length,
                };
            })
            .filter(b => b.count > 0)
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [affiliateCoupons, businessLookup]);

    // console.log("business with coupons", businessesWithCoupons)

    const displayedCoupons = useMemo(() => {
        const base = selectedBusinessId === 'all'
            ? allCoupons
            : allCoupons.filter(c => String(c.business_id) === String(selectedBusinessId));
        return base.filter(c => !deletedIds.has(c.localId));
    }, [allCoupons, selectedBusinessId, deletedIds]);

    const { fetching, fetched } = affiliateCoupons || {};
    const loadedCount = Object.values((affiliateCoupons && affiliateCoupons.byBusiness) || {}).filter(s => s.fetched).length;
    const totalCount = Object.keys((affiliateCoupons && affiliateCoupons.byBusiness) || {}).length;

    return (
        <AppWrapper
            showHeader
            showSideNav
            showHeadingRow
            headingText="Affiliate Coupons"
        >
            <div className="col-md-12">
                <Breadcrumbs locationArray={['Admin', 'Affiliate Coupons']} />

                <div className={styles.statusBar}>
                    {fetching && (
                        <span className={styles.statusLoading}>
                            ⏳ Loading coupons… {loadedCount}/{totalCount} businesses fetched
                        </span>
                    )}
                    {fetched && !fetching && (
                        <span className={styles.statusDone}>
                            ✅ {allCoupons.length} coupons loaded across {businessesWithCoupons.length} businesses
                        </span>
                    )}
                </div>

                {deleteStatus && !deleteStatus.pending && (
                    <div style={{
                        padding: '8px 16px',
                        marginBottom: 12,
                        borderRadius: 4,
                        background: deleteStatus.success ? '#d4edda' : '#f8d7da',
                        border: `1px solid ${deleteStatus.success ? '#28a745' : '#dc3545'}`,
                        color: deleteStatus.success ? '#155724' : '#721c24',
                        fontSize: 14,
                    }}>
                        {deleteStatus.success ? '✅' : '❌'} {deleteStatus.message}
                    </div>
                )}

                <div className={styles.filterRow}>
                    <label className={styles.filterLabel}>Filter by business:</label>
                    <select
                        className={styles.filterSelect}
                        value={selectedBusinessId}
                        onChange={e => setSelectedBusinessId(e.target.value)}
                    >
                        <option value="all">All businesses ({displayedCoupons.length} coupons)</option>
                        {businessesWithCoupons.map(b => (
                            <option key={b.id} value={b.id}>
                                {b.name} ({allCoupons.filter(c => String(c.business_id) === String(b.id) && !deletedIds.has(c.localId)).length} coupons)
                            </option>
                        ))}
                    </select>
                </div>

                {selectedBusinessId === 'all' ? (
                    businessesWithCoupons.map(b => {
                        const bizCoupons = displayedCoupons.filter(c => String(c.business_id) === String(b.id));
                        if (!bizCoupons.length) return null;
                        return (
                            <ErrorBoundary key={b.id} businessName={b.name}>
                                <div className={styles.businessGroup}>
                                    <div className={styles.businessGroupHeader}>
                                        <h5 className={styles.businessGroupTitle}>{b.name}</h5>
                                        <span className={styles.businessGroupCount}>{bizCoupons.length} coupons</span>
                                    </div>
                                    <AffiliateCouponsList
                                        coupons={bizCoupons}
                                        loading={false}
                                        dispatch={dispatch}
                                        handleRemove={(localId) => handleDelete(localId)}
                                        affiliateBusinesses={Object.values(businessLookup)}
                                        selectedBusinessId={b.id}
                                        selectedBusiness={businessLookup[b.id]}
                                        inputValue={b.affiliate_code}
                                        editMode={true}
                                        skipLocalStoragePublished={true}
                                    />
                                </div>
                            </ErrorBoundary>
                        );
                    })
                ) : (
                    <ErrorBoundary businessName="selected business">
                        <div className={styles.businessGroup}>
                            <AffiliateCouponsList
                                coupons={displayedCoupons}
                                loading={false}
                                dispatch={dispatch}
                                handleRemove={(localId) => handleDelete(localId)}
                                affiliateBusinesses={Object.values(businessLookup)}
                                selectedBusinessId={selectedBusinessId}
                                selectedBusiness={businessLookup[selectedBusinessId]}
                                inputValue=""
                            />
                        </div>
                    </ErrorBoundary>
                )}

                {fetched && displayedCoupons.length === 0 && (
                    <div className={styles.emptyState}>
                        No affiliate coupons found.
                    </div>
                )}
            </div>
        </AppWrapper>
    );
};

const mapStateToProps = state => ({
    affiliateCoupons: state.coupons.affiliateCoupons,
    affiliateBusinessData: state.affiliateBusiness && state.affiliateBusiness.data,
});

export default connect(mapStateToProps)(AffiliateCouponViewer);